import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import {
    useApolloClient, useLazyQuery, gql, useReactiveVar,
} from '@apollo/client';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { regionSubdivisionVar, selectedPeriodVar } from 'app/apollo/reaction';
import { useMenuPeriodsData } from 'app/hooks/accessors';
import { isDateInRange } from 'app/containers/contexts/filters.context/filters.context';
import { ConfigContext } from './ConfigProvider';


const getFirstPeriod = (config) => {
    const {
        firstPeriodStart, defaultPeriodStart, periods, ignorePeriodFromUrl,
    } = config;
    const { period: periodFromUrl } = queryString.parse(window.location.search);

    if (firstPeriodStart && ignorePeriodFromUrl) {
        return firstPeriodStart;
    }
    if (periodFromUrl) {
        return periodFromUrl;
    }
    if (defaultPeriodStart) {
        return defaultPeriodStart;
    }
    if (firstPeriodStart) {
        return firstPeriodStart;
    }
    if (periods && periods.length) {
        return periods[0].period;
    }
    return null;
};

const updateUrlPeriodParam = (period) => {
    const params = queryString.parse(window.location.search);
    const nextParams = {
        ...params,
        period,
    };
    const nextQuery = queryString.stringify(nextParams);
    const { pathname, hash } = window.location.pathname;

    window.history.replaceState('', '', `${pathname ?? ''}?${nextQuery}${hash ?? ''}`);
};

const getVisiblePeriods = (periods, visibleStatuses) => {
    const result = periods
        .filter((p) => {
            if (visibleStatuses) {
                return visibleStatuses.includes(p.isVisibleInFilter);
            }
            return p.isVisibleInFilter === 'visible';
        });
    return result;
};


export const FiltersContext = createContext({});

// TODO: Поле period и config
export function FiltersProvider(props) {
    const { children } = props;

    const { periods } = useMenuPeriodsData();

    const { pageId } = useContext(ConfigContext);

    const isNYPage = useMemo(() => pageId === 'blyuda-dlya-novogodnego-stola' || pageId === 'old_ny' || pageId === 'christmas', [pageId]);

    const [currentPeriods, setCurrentPeriods] = useState(periods);

    const regionSubdivision = useReactiveVar(regionSubdivisionVar);

    const [updatePeriods] = useLazyQuery(
        gql`query PERIODS_BY_SUBDIVISION(
                    $subdivision: countrySubdivision!
                ) {
                    menuSidebar(
                        subdivision: $subdivision
                    ) {
                            periods {
                            start
                            end
                            isVisibleInFilter
                            categories {
                                id
                                title
                                description
                            }
                            tags {
                                id
                                title
                                categoryId: category_id
                                categoryName: category_name
                                erp_id
                            }
                        }
                        subdivision
                    }
                    }
                `,
        {
            variables: {
                subdivision: regionSubdivisionVar()?.replace(/-/g, '_'),
            },
            fetchPolicy: 'network-only',
        },
    );

    useEffect(
        () => {
            updatePeriods().then((r) => {
                if (r?.data?.menuSidebar?.periods) {
                    setCurrentPeriods(r.data.menuSidebar?.periods);
                }
            });
        },
        [regionSubdivision, updatePeriods, isNYPage],
    );

    const client = useApolloClient();
    const config = useContext(ConfigContext);

    const {
        period,
        periodVisibilityStatus,
        defaultTrialScenario,
        periodVisibilityFilterFunc,
    } = config;

    const [filtersState, setFilterState] = useState({
        period,
        initialPeriod: period,
        visibilityState: 'visible',
        visiblePeriods: [],
    });

    useEffect(() => {
        if (period) return;

        let newPeriods = null;

        const visibleStatuses = [
            'visible',
        ];
        if (periodVisibilityStatus) {
            visibleStatuses.push(periodVisibilityStatus);
        }

        const firstPeriodStart = getFirstPeriod(config);

        if (firstPeriodStart) {
            const firstPeriodIndex = currentPeriods.findIndex(
                ({ start: periodStart, isVisibleInFilter }) => {
                    if (defaultTrialScenario) {
                        return isDateInRange(firstPeriodStart, periodStart, 1);
                    }

                    const isVisible = visibleStatuses.includes(isVisibleInFilter);

                    return isVisible && isDateInRange(firstPeriodStart, periodStart, 1);
                },
            );

            const hasPeriod = firstPeriodIndex >= 0;
            newPeriods = hasPeriod
                ? getVisiblePeriods(currentPeriods.slice(firstPeriodIndex), visibleStatuses)
                : getVisiblePeriods(currentPeriods, visibleStatuses);
        } else {
            newPeriods = getVisiblePeriods(currentPeriods, visibleStatuses);
        }

        if (periodVisibilityFilterFunc) {
            newPeriods = periodVisibilityFilterFunc(newPeriods);
        }

        const visiblePeriods = (() => {
            const visiblebyStatus = getVisiblePeriods(currentPeriods, visibleStatuses);
            if (periodVisibilityFilterFunc) {
                return periodVisibilityFilterFunc(visiblebyStatus);
            }
            return visiblebyStatus;
        })();

        const { start, isVisibleInFilter } = newPeriods[0];
        setFilterState({
            period: start,
            initialPeriod: start,
            visibilityState: isVisibleInFilter,
            visiblePeriods,
        });

        updateUrlPeriodParam(start);
    }, [client, config, defaultTrialScenario, period, currentPeriods, periodVisibilityFilterFunc, periodVisibilityStatus]);

    const changePeriod = useCallback((nextPeriod) => {
        console.log(nextPeriod, 'nextPeriod to set');
        // setFilterState({
        //     ...filtersState,
        //     period: nextPeriod,
        // });
    }, [filtersState]);

    const setNextAvailablePeriod = useCallback(
        () => {
            const { visiblePeriods } = filtersState;
            const currentPeriodIndex = filtersState.visiblePeriods?.findIndex(
                (availablePeriod) => availablePeriod?.start === filtersState.period,
            );

            if (currentPeriodIndex > -1 && visiblePeriods.length > (currentPeriodIndex + 1)) {
                window.location = `${window.location.pathname}?period=${visiblePeriods[currentPeriodIndex + 1]?.start}`;
            }
        },
        [filtersState],
    );
    useEffect(() => {
        selectedPeriodVar(filtersState.period);
    }, [filtersState.period]);


    const contextValue = useMemo(() => {
        const val = {
            ...filtersState,
            changePeriod,
            setNextAvailablePeriod,
        };
        return val;
    }, [filtersState, changePeriod, setNextAvailablePeriod]);

    return (
        <FiltersContext.Provider value={contextValue}>
            {filtersState.period && children}
        </FiltersContext.Provider>
    );
}

FiltersProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};
